/**
 * @ipc/validation · src/schemas/webhooks.ts — группа `/api/v1/webhooks` (раздел 08).
 * Тело идентично `@ipc/webhooks` `api.schemas.ts` (CreateSubscriptionSchema/
 * UpdateSubscriptionSchema/DeliveryListQuerySchema) — та же граница, backend
 * валидирует независимо своей копией. Формы admin-ui получают русские
 * самодостаточные сообщения для мгновенного feedback (react-hook-form + zodResolver).
 *
 * Bulk: POST /webhooks/subscriptions/bulk (disable|delete).
 */
import { z } from 'zod/v4';
import { paginationSchema, searchSchema, sortOrderSchema, booleanQuerySchema } from '../common/primitives.ts';

/** Каталог исходящих событий (зеркало OUTBOUND_EVENT_TYPES из @ipc/webhooks). */
export const OUTBOUND_EVENT_TYPES = [
  'payment.succeeded',
  'payment.failed',
  'payment.refunded',
  'invoice.created',
  'refund.completed',
] as const;
export type OutboundEventType = (typeof OUTBOUND_EVENT_TYPES)[number];

export const outboundEventTypeSchema = z.enum(OUTBOUND_EVENT_TYPES);

/** Канон DeliveryStatus (раздел 02) — используется фильтром журнала доставок. */
export const DELIVERY_STATUSES = ['PENDING', 'DELIVERING', 'DELIVERED', 'RETRYING', 'FAILED'] as const;
export const deliveryStatusSchema = z.enum(DELIVERY_STATUSES);
export type DeliveryStatus = z.infer<typeof deliveryStatusSchema>;

/** Форма создания подписки: POST /api/v1/webhooks/subscriptions (webhooks.manage). */
export const createSubscriptionFormSchema = z.object({
  url: z
    .string({ error: 'Укажите URL приёмника' })
    .url('Укажите корректный URL')
    .max(2048, 'URL не длиннее 2048 символов')
    .refine((u) => u.startsWith('https://'), 'URL должен быть https'),
  events: z.array(outboundEventTypeSchema).min(1, 'Выберите хотя бы одно событие'),
  merchantId: z.coerce
    .number({ error: 'Укажите ID мерчанта' })
    .int('ID мерчанта — целое число')
    .positive('ID мерчанта — положительное число'),
  description: z.string().max(500, 'Не длиннее 500 символов').optional(),
});
export type CreateSubscriptionFormDto = z.infer<typeof createSubscriptionFormSchema>;

/** Форма редактирования подписки: PATCH /api/v1/webhooks/subscriptions/:id. */
export const updateSubscriptionFormSchema = z.object({
  url: z
    .string({ error: 'Укажите URL приёмника' })
    .url('Укажите корректный URL')
    .max(2048, 'URL не длиннее 2048 символов')
    .refine((u) => u.startsWith('https://'), 'URL должен быть https'),
  events: z.array(outboundEventTypeSchema).min(1, 'Выберите хотя бы одно событие'),
  active: z.boolean(),
});
export type UpdateSubscriptionFormDto = z.infer<typeof updateSubscriptionFormSchema>;

/**
 * GET /webhooks/subscriptions — page-based list filters (зеркало
 * `@ipc/webhooks` SubscriptionListQuerySchema; dual-source DTO).
 */
export const subscriptionFiltersSchema = paginationSchema.extend({
  search: searchSchema,
  merchantId: z.coerce.number().int().positive().optional(),
  isActive: booleanQuerySchema,
  sortBy: z.enum(['createdAt', 'id']).default('createdAt'),
  sortOrder: sortOrderSchema,
});
export type SubscriptionFiltersDto = z.infer<typeof subscriptionFiltersSchema>;

/**
 * POST /webhooks/subscriptions/bulk — массовое отключение или удаление.
 * `disable` = isActive=false (мягкая пауза); `delete` = hard delete.
 */
export const subscriptionBulkSchema = z.object({
  ids: z.array(z.number().int().positive()).min(1).max(100),
  action: z.enum(['disable', 'delete']),
});
export type SubscriptionBulkDto = z.infer<typeof subscriptionBulkSchema>;
